import React, { useState, useEffect, useMemo } from "react";
import clsx from "clsx";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import Donut from "components/Charts/Donut";
import Line from "components/Charts/Line";
import { differenceInDaysOrNA } from "utils/date-utils";
import { getAlertStatus, AlertEnum, getWorkDaysOff } from "utils/horse";
import useStyles from "./styles";

export interface Props {
  horse: racehorse360.IHorse;
  type: "line" | "donut" | "text";
  variant?: "bold" | "default";
  simplified?: boolean;
  className?: string;
}

const DaysOffDiagram = (props: Props) => {
  const { horse, type, className, simplified, variant = "default" } = props;
  const classes = useStyles();

  const [workOff, setWorkOff] = useState<number>();
  const isDonut = type === "donut";
  const isLine = type === "line";
  const isText = type === "text";
  const redLimit = horse.currentFacility?.workoutRequiredDaysOff || 90;

  useEffect(() => {
    const workOff = getWorkDaysOff(horse);
    Number.isInteger(workOff) && setWorkOff(Number(workOff));
  }, [horse.lastRaceDate, horse.lastWorkoutDate]);

  const memoizedDetails = useMemo(() => {
    const raceOff = differenceInDaysOrNA(horse.lastRaceDate);
    const workOff = getWorkDaysOff(horse);
    const status = getAlertStatus(
      Number.isInteger(workOff) ? Number(workOff) : Number.NaN,
      redLimit
    );
    return simplified ? (
      <span className={clsx(classes.text, `${className}__text`, status)}>
        {workOff}
      </span>
    ) : (
      <div
        className={clsx(classes.text, `${className}__text`, {
          [AlertEnum.na]: status === AlertEnum.na
        })}
      >
        {raceOff}
        {" / "}
        <span className={clsx(classes.text, `${className}__text`, status)}>
          {workOff}
        </span>
      </div>
    );
  }, [horse.lastRaceDate, horse.lastWorkoutDate, simplified]);

  return (
    <>
      {isDonut && <Donut value={workOff} capacity={redLimit} />}
      {isLine && (
        <Line
          value={workOff}
          capacity={redLimit}
          title={memoizedDetails}
          variant={variant}
        />
      )}
      {isText && <div className={className}>{memoizedDetails}</div>}
    </>
  );
};

export default DaysOffDiagram;
